import React from 'react';
import { Link } from 'react-router-dom';

const LendersLanding: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-800 text-white py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Lenders & Financial Partners
                        </h1>
                        <p className="text-xl md:text-2xl text-purple-100 max-w-4xl mx-auto mb-8">
                            Power the NYC energy transition with data-driven lending decisions and risk assessment for LL97 retrofit projects
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link 
                                to="/lender-partnership-signup" 
                                className="bg-white text-purple-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                            >
                                🏦 Partner With Us
                            </Link>
                            <Link 
                                to="/target-properties" 
                                className="bg-purple-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-400 border-2 border-white transition-colors"
                            >
                                📊 View Market Data
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Market Opportunity Section */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            The $2.5B LL97 Financing Opportunity
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Local Law 97 creates unprecedented demand for energy retrofit financing across NYC's building stock
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-purple-600 mb-2">$2.5B+</div>
                            <div className="text-gray-600">Total Market Opportunity</div>
                            <div className="text-sm text-gray-500 mt-2">Retrofit investment needed</div>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-purple-600 mb-2">64,000+</div>
                            <div className="text-gray-600">Buildings Need Financing</div>
                            <div className="text-sm text-gray-500 mt-2">LL97 compliance required</div>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-purple-600 mb-2">$150K</div>
                            <div className="text-gray-600">Average Project Size</div>
                            <div className="text-sm text-gray-500 mt-2">Typical retrofit loan</div>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6">
                            <div className="text-3xl font-bold text-purple-600 mb-2">2024</div>
                            <div className="text-gray-600">Financing Rush Begins</div>
                            <div className="text-sm text-gray-500 mt-2">When fines start</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Value Proposition */}
            <div className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                Why Partner With Us?
                            </h2>
                            <div className="space-y-6">
                                <div className="flex items-start space-x-4">
                                    <div className="text-purple-600 text-xl bg-purple-100 p-2 rounded-lg">📊</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Risk Assessment Data</h3>
                                        <p className="text-gray-600">
                                            Access comprehensive building energy profiles, compliance status, and retrofit ROI projections for informed lending decisions.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="text-purple-600 text-xl bg-purple-100 p-2 rounded-lg">🎯</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Qualified Borrowers</h3>
                                        <p className="text-gray-600">
                                            Connect with property owners who have completed energy assessments and are ready to move forward with projects.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="text-purple-600 text-xl bg-purple-100 p-2 rounded-lg">💼</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Portfolio Diversification</h3>
                                        <p className="text-gray-600">
                                            Expand into the growing green finance market with projects that provide environmental and financial returns.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="text-purple-600 text-xl bg-purple-100 p-2 rounded-lg">🔒</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Reduced Default Risk</h3>
                                        <p className="text-gray-600">
                                            Energy efficiency improvements increase property values and cash flow, reducing loan default risk.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Partnership Benefits</h3>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Access to pre-qualified borrowers</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Detailed project feasibility data</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Risk assessment tools and analytics</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Market intelligence and trends</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Co-marketing opportunities</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="text-green-500">✓</span>
                                    <span className="text-gray-700">Technical support for underwriting</span>
                                </div>
                            </div>
                            <div className="mt-6">
                                <Link 
                                    to="/lender-partnership-signup" 
                                    className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-purple-700 transition-colors block text-center"
                                >
                                    Become a Partner Lender
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lending Products & Services */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            High-Demand Financing Products
                        </h2>
                        <p className="text-xl text-gray-600">
                            LL97 compliance creates demand for diverse financing solutions
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🏢</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Commercial Property Loans</h3>
                            <p className="text-gray-600 mb-4">
                                Traditional and green commercial mortgages for energy-efficient buildings and retrofit projects.
                            </p>
                            <div className="space-y-2 text-sm text-gray-500">
                                <div>• $500K - $50M+ loan amounts</div>
                                <div>• 5-25 year terms</div>
                                <div>• Competitive green rates</div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🔧</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Equipment Financing</h3>
                            <p className="text-gray-600 mb-4">
                                Specialized financing for HVAC systems, lighting upgrades, and energy efficiency equipment.
                            </p>
                            <div className="space-y-2 text-sm text-gray-500">
                                <div>• $25K - $2M typical range</div>
                                <div>• Equipment as collateral</div>
                                <div>• Fast approval process</div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">💳</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Lines of Credit</h3>
                            <p className="text-gray-600 mb-4">
                                Flexible credit lines for ongoing energy improvements and maintenance of efficiency measures.
                            </p>
                            <div className="space-y-2 text-sm text-gray-500">
                                <div>• $50K - $5M credit limits</div>
                                <div>• Draw as needed</div>
                                <div>• Interest-only options</div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🌱</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Green Construction Loans</h3>
                            <p className="text-gray-600 mb-4">
                                Short-term financing for major retrofit projects with conversion to permanent financing.
                            </p>
                            <div className="space-y-2 text-sm text-gray-500">
                                <div>• Construction-to-perm options</div>
                                <div>• Interest-only during construction</div>
                                <div>• Green building certifications</div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">📋</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">PACE Financing</h3>
                            <p className="text-gray-600 mb-4">
                                Property Assessed Clean Energy financing for long-term energy efficiency improvements.
                            </p>
                            <div className="space-y-2 text-sm text-gray-500">
                                <div>• 15-25 year terms</div>
                                <div>• Property tax assessment</div>
                                <div>• Transferable to new owners</div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">💰</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Bridge Financing</h3>
                            <p className="text-gray-600 mb-4">
                                Short-term financing to avoid LL97 fines while permanent solutions are implemented.
                            </p>
                            <div className="space-y-2 text-sm text-gray-500">
                                <div>• 6-24 month terms</div>
                                <div>• Fast approval and closing</div>
                                <div>• Refinance options</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tools & Data Access */}
            <div className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Underwriting Tools & Market Intelligence
                        </h2>
                        <p className="text-xl text-gray-600">
                            Access the data and analytics you need for informed lending decisions
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">📊</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Building Energy Profiles</h3>
                            <p className="text-gray-600 mb-4">
                                Comprehensive energy usage data, LL97 compliance status, and fine projections for 64,000+ NYC buildings.
                            </p>
                            <Link 
                                to="/building-analysis" 
                                className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
                            >
                                Explore Building Data →
                            </Link>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">🎯</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Target Properties Analysis</h3>
                            <p className="text-gray-600 mb-4">
                                Identify properties most likely to need financing for LL97 compliance retrofits.
                            </p>
                            <Link 
                                to="/target-properties" 
                                className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
                            >
                                View Target Properties →
                            </Link>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">🔧</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Retrofit ROI Calculator</h3>
                            <p className="text-gray-600 mb-4">
                                Calculate energy savings, project costs, and financial returns for different retrofit measures.
                            </p>
                            <Link 
                                to="/retrofit-calculator" 
                                className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
                            >
                                Use ROI Calculator →
                            </Link>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-2xl mb-4">💰</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">LL97 Fine Calculator</h3>
                            <p className="text-gray-600 mb-4">
                                Assess potential fine exposure and understand the financial incentive for borrowers to invest in retrofits.
                            </p>
                            <Link 
                                to="/ll97-calculator" 
                                className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
                            >
                                Calculate Fines →
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Stories */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Lender Success Stories
                        </h2>
                        <p className="text-xl text-gray-600">
                            Financial institutions growing their green lending portfolios
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-xl mb-4">🏦</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Community Bank - Manhattan</h3>
                            <p className="text-gray-600 mb-4">
                                "Originated $15M in green commercial loans in 6 months using the platform's building data for risk assessment."
                            </p>
                            <div className="text-sm text-gray-500">
                                — VP Commercial Lending
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-3xl mb-4">💳</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Equipment Finance Company</h3>
                            <p className="text-gray-600 mb-4">
                                "The retrofit calculator helps us price deals accurately. Our LL97 portfolio has 40% lower default rates."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Chief Risk Officer
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6">
                            <div className="text-xl mb-4">🌱</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Green Investment Fund</h3>
                            <p className="text-gray-600 mb-4">
                                "Platform data gives us competitive advantage in identifying and underwriting the best energy efficiency deals."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Investment Director
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="py-16 bg-gradient-to-r from-purple-600 to-blue-800 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Power NYC's Energy Transformation
                    </h2>
                    <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
                        Partner with us to capture the massive LL97 financing opportunity while supporting sustainable building improvements across New York City.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link 
                            to="/lender-partnership-signup" 
                            className="bg-white text-purple-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                        >
                            🏦 Become a Partner
                        </Link>
                        <Link 
                            to="/contact" 
                            className="bg-purple-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-400 border-2 border-white transition-colors"
                        >
                            📞 Schedule Demo
                        </Link>
                    </div>
                    <p className="text-sm text-purple-200 mt-6">
                        No upfront costs • Risk-free partnership • Immediate market access
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LendersLanding;
